class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector('.search_details');
    this.predictiveSearch = this.querySelector('predictive-search')
    this.isOpen = false;
    this.quickSearchTrending = this.querySelectorAll('.quickSearchTrending .link');

    this.setupEventListeners();
    this.quickSearchTrending.forEach(element => {
      element.addEventListener('click', this.quickSearchTrendingClick.bind(this));
    });
  }
  
  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));
    
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 500).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));
    if (this.closeStickySearchButton != null) {
      this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  getWildcardQuery(searchTerm) {
    return searchTerm.endsWith('*') ? searchTerm : `${searchTerm}*`;
  }

  getCleanQuery(searchTerm) {
    return searchTerm.replace(/\*+$/g, '');
  }

  isReferenceSearchTerm(searchTerm) {
    const cleanQuery = this.getCleanQuery(searchTerm).trim();

    if (!/^[A-Za-z0-9][A-Za-z0-9._-]{3,}$/.test(cleanQuery)) return false;

    return (/[A-Za-z]/.test(cleanQuery) && /\d/.test(cleanQuery)) || /^\d{5,}$/.test(cleanQuery);
  }

  getExactReferenceQuery(searchTerm) {
    return this.getCleanQuery(searchTerm).trim();
  }

  getSearchQuery(searchTerm) {
    return this.isReferenceSearchTerm(searchTerm)
      ? this.getExactReferenceQuery(searchTerm)
      : this.getWildcardQuery(searchTerm);
  }

  getSearchUrl(baseUrl, searchTerm, additionalParams = {}) {
    const params = new URLSearchParams(additionalParams);
    const referenceCode = window.getReferenceCode(searchTerm);

    params.set('q', this.getSearchQuery(searchTerm));
    params.set('type', 'product');

    if (referenceCode) {
      params.set('filter.p.m.custom.referencia', referenceCode);
    } else if (!this.isReferenceSearchTerm(searchTerm)) {
      params.set('options[prefix]', 'last');
    }

    return `${baseUrl}?${params.toString()}`;
  }

  onChange() {
    const searchTerm = this.getQuery();
    
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const searchTerm = this.getQuery();

    if (!searchTerm.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    if (this.isReferenceSearchTerm(searchTerm)) {
      event.preventDefault();
      window.location.href = this.getSearchUrl(routes.search_url, searchTerm);
      return;
    }

    this.input.value = this.getWildcardQuery(searchTerm);
  }

  onFocus() {
    const searchTerm = this.getQuery();
    
    if (!searchTerm.length) return this.showTrendingAndProducts();

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
        // this.hideTrendingAndProducts();
      };
    })
  }
  
  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.showTrendingAndProducts();
    };
    event.preventDefault();
    
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';
    
    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }
    
  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }
  
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (window.getReferenceCode(searchTerm)) {
      return fetch(this.getSearchUrl(routes.search_url, searchTerm, { view: 'reference_cards' }))
        .then(response => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(html => {
          if (this.getQuery() !== searchTerm) return;
          const results = new DOMParser().parseFromString(html, 'text/html').querySelector('#predictive-search-results');
          if (!results) throw new Error('Missing reference search results');
          results.querySelectorAll('[data-qs-view-all-link], .search-more').forEach(link => {
            link.href = this.getSearchUrl(routes.search_url, searchTerm);
          });
          this.renderSearchResults(results.outerHTML);
        })
        .catch(error => {
          if (this.getQuery() !== searchTerm) return;
          this.close();
          console.error('Reference search failed', error);
        });
    }
    
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.updateViewAllLink(searchTerm);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(this.getSearchQuery(searchTerm))}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=${this.productToShow}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        
        return response.text();
      })  
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        const hasPredictiveProducts = new DOMParser()
          .parseFromString(resultsMarkup, 'text/html')
          .querySelector('.products-grid .product');

        if (!hasPredictiveProducts) {
          return this.getSearchResultsFallback(searchTerm, queryKey, resultsMarkup);
        }

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
        this.updateViewAllLink(searchTerm);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  getSearchResultsFallback(searchTerm, queryKey, emptyResultsMarkup) {
    return fetch(this.getSearchUrl(routes.search_url, searchTerm, { view: 'ajax_quick_search' }))
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.json();
      })
      .then((data) => {
        if (!data.results || !data.results.length) {
          this.cachedResults[queryKey] = emptyResultsMarkup;
          this.renderSearchResults(emptyResultsMarkup);
          this.updateViewAllLink(searchTerm);
          return;
        }

        const productHandles = data.results
          .map((product) => product.handle)
          .filter(Boolean)
          .slice(0, Number(this.productToShow) || data.results.length);
        const root = (routes.root || '').replace(/\/$/, '');

        return Promise.all(productHandles.map((handle, index) => (
          fetch(`${root}/products/${handle}?view=ajax_card`)
            .then((response) => {
              if (!response.ok) throw new Error(response.status);

              return response.text();
            })
            .then((cardMarkup) => `
              <div class="product ${index + 1}">
                ${cardMarkup}
              </div>
            `)
            .catch(() => null)
        )))
          .then((productCards) => {
            const productCardsMarkup = productCards.filter(Boolean).join('');
            if (!productCardsMarkup) {
              this.cachedResults[queryKey] = emptyResultsMarkup;
              this.renderSearchResults(emptyResultsMarkup);
              this.updateViewAllLink(searchTerm);
              return;
            }

            const resultsMarkup = this.buildFallbackResultsMarkup(productCardsMarkup, data.results_count, searchTerm, emptyResultsMarkup);

            this.cachedResults[queryKey] = resultsMarkup;
            this.renderSearchResults(resultsMarkup);
            this.updateViewAllLink(searchTerm);
          });
      });
  }

  buildFallbackResultsMarkup(productCardsMarkup, resultsCount, searchTerm, emptyResultsMarkup) {
    const cleanQuery = this.getCleanQuery(searchTerm);
    const fallbackDocument = new DOMParser().parseFromString(emptyResultsMarkup, 'text/html');
    const headingText = fallbackDocument.querySelector('.search-block-title .text')?.textContent || '';

    return `
      <div id="predictive-search-results" class="quickSearchResults quickSearchResultsBlock custom-scrollbar">
        <div class="container">
          <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing search-block-title text-left">
            <span class="text">${headingText}</span>
            <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </h2>
          <div id="predictive-search-results-list" class="predictive-search__results-list list-unstyled" role="listbox" aria-labelledby="predictive-search-products">
            <div class="products-grid column-${window.settings?.search_product_per_row || 4} disable-srollbar text-left">
              ${productCardsMarkup}
            </div>
            <div id="predictive-search-option-search-keywords" class="predictive-search__list-item productViewAll" role="option">
              <a href="${this.getSearchUrl(routes.search_url, cleanQuery)}" class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow button button-view-all" tabindex="-1" data-qs-view-all-link>${resultsCount}</a>
            </div>
            <div class="predictive-search__loading-state" aria-hidden="true">
              <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
          </div>
          <span class="hidden" data-predictive-search-live-region-count-value>${resultsCount}</span>
        </div>
      </div>
    `;
  }
  
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setAttribute('open', true);
    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  
  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('[class^="header-navigation"]').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
  
  open() {
    //this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 
    this.trendingAndProductsBlock.classList.add('is-show')
    this.trendingAndProductsBlock.classList.remove('hidden');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 

    this.trendingAndProductsBlock.classList.remove('is-show')
    this.trendingAndProductsBlock.classList.add('hidden');
  }

  onDocClick(e) {
    const isInModal = this.contains(e.target), $target = e.target

    if (!isInModal || $target.closest('.header-search-popup-close') || $target.matches('.header-search-popup-close') || $target.closest('.header-search-close') || $target.matches('.header-search-close')) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();
    document.querySelector('body').classList.remove('sticky-search-open');
    document.querySelector('body').classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-open')
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-custom-open')
  }

  updateViewAllLink(searchTerm) {
    const qsViewAllLink = this.querySelector('[data-qs-view-all-link]');
    if (!qsViewAllLink) return 

    const linkTotal = this.getSearchUrl(routes.search_url, searchTerm)
    
    qsViewAllLink.href = linkTotal
    return this.getTotalResults(linkTotal).then(count => {
      qsViewAllLink.innerHTML = qsViewAllLink.innerHTML.replace('()', `(${count})`)
    });
  }

  getTotalResults(url) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })  
      .then((text) => {
        const totalCount = new DOMParser().parseFromString(text, 'text/html').querySelector('[id^="SearchSection"]').dataset.searchCount;
        return totalCount
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  quickSearchTrendingClick(event) {
    window.location = event.currentTarget.getAttribute('href');
  }
}

customElements.define('predictive-search', PredictiveSearch);
